/**
 * Superlatives (SPEC §2.6) — "free content; the 'wait, WHAT' beat that drives
 * replay". The protocol already computes all four (`computeSuperlatives`); this
 * only stages them. A superlative with no eligible data comes back with an
 * empty `playerIds` and must render as "not awarded" rather than crash.
 */
import { motion } from 'motion/react';
import type { PlayerId, Superlative } from '@idgame/protocol';
import { LicenseCard, type LicensePlayer } from './LicenseCard.js';
import { Eyebrow } from './ui.js';

export function SuperlativeList({
  superlatives,
  players,
  selfId,
}: {
  superlatives: readonly Superlative[];
  players: ReadonlyMap<PlayerId, LicensePlayer>;
  selfId?: PlayerId;
}) {
  return (
    <div className="space-y-2.5" data-testid="superlatives">
      {superlatives.map((superlative, index) => (
        <motion.section
          key={superlative.kind}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + index * 0.35, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          data-testid={`superlative-${superlative.kind}`}
          className="rounded-card border border-line bg-cream p-3"
        >
          <Eyebrow>{superlative.title}</Eyebrow>
          {superlative.playerIds.length === 0 ? (
            <p className="mt-2 text-[0.8125rem] text-ink-soft">
              Not awarded — {superlative.detail}.
            </p>
          ) : (
            <>
              <div className="mt-2 space-y-1.5">
                {superlative.playerIds.map((id) => {
                  const player = players.get(id);
                  if (!player) return null;
                  return (
                    <LicenseCard
                      key={id}
                      player={{ ...player, isSelf: id === selfId }}
                      variant="chip"
                      tone="top"
                    />
                  );
                })}
              </div>
              <p className="mt-2 text-[0.8125rem] text-ink-soft">{superlative.detail}</p>
            </>
          )}
        </motion.section>
      ))}
    </div>
  );
}
